import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type { DockviewApi } from "dockview-react";
import { activeWorkspace, searchSymbols, setWorkspaceLocked, type SymbolMatch } from "@helm/core";
import { Icon } from "../components/Icon";
import { TimeframePicker } from "../chart/TimeframePicker";
import { lazyComponent } from "../components/lazy";
import { OverlayPicker } from "../chart/OverlayPicker";
import { useAccountCounts } from "../viewmodel/account";
import { AddModuleButton } from "../workspace/WorkspaceBar";
import { workspaceActions } from "../workspace/Workspace";
import { useWorkspaces, type WorkspaceStore } from "../workspace/workspaceStore";
import { openSidebarTab } from "../sidebar/events";
import { useActiveChart } from "./activeChart";
import "./toolbar.css";

/** Market-data feed shown after the symbol (mockup "es1! @ rithmic"). */
const FEED = "rithmic";

// The indicators button needs the indicator registry (every built-in), which loads with the charts;
// it shows only while a chart is active, so its chunk is in by then (audit S-12 budget).
const IndicatorsButton = lazyComponent(
  () => import("../chart/indicators/IndicatorsButton").then((m) => m.IndicatorsButton),
  "indicators",
);

/**
 * The window-wide top toolbar (Glacis mockups 01/02; owner 2026-09-28, Q-G3), in both visual
 * styles, hideable in Settings → Appearance. It drives the ACTIVE chart (toolbar/activeChart.ts:
 * the chart module used last, and its active pane): symbol @ feed, pinned timeframes + the full
 * menu, ƒx Indicators, layers, chart layout; then Orders / Positions (count) which focus those
 * modules, Alert (the right sidebar's Alerts tab), and the workspace "Lock layout" and "+ Add
 * module". The account-level Flatten is the position strip's (owner 2026-10-04 UI clean-up).
 *
 * While it shows, chart headers keep a compact timeframe menu (their own pane stays controllable)
 * and the workspace bar drops the Lock layout / Add module it would duplicate.
 */
export function TopToolbar({
  store,
  dock,
}: {
  store: WorkspaceStore;
  dock: () => DockviewApi | null;
}): React.JSX.Element {
  const chart = useActiveChart();
  const snap = chart?.snapshot ?? null;
  const view = useAccountCounts();
  const { doc } = useWorkspaces(store);
  const ws = activeWorkspace(doc);
  const noChart = "No chart in this workspace — add one with + (the toolbar drives the chart you used last)";
  const paneNote =
    snap && snap.paneCount > 1 ? ` · pane ${String(snap.pane + 1)} of ${String(snap.paneCount)}` : "";
  return (
    <div className="toptool" role="toolbar" aria-label="Chart toolbar (active chart)">
      <SymbolBox
        symbol={snap?.symbol ?? null}
        disabled={chart === null}
        title={chart ? `Active chart symbol${paneNote} — type to change` : noChart}
        onCommit={(s) => {
          chart?.setSymbol(s);
        }}
      />
      <span className="toptool-sep" aria-hidden="true" />
      {snap && chart ? (
        <>
          <TimeframePicker
            value={snap.tf}
            onChange={chart.setTimeframe}
            rangePhantoms={snap.rangePhantoms}
            onRangePhantomsChange={chart.setRangePhantoms}
          />
          <span className="toptool-sep" aria-hidden="true" />
          <IndicatorsButton value={snap.indicators} onChange={chart.setIndicators} />
          <OverlayPicker value={snap.overlays} onChange={chart.setOverlays} />
          {chart.openCompare && (
            <button
              type="button"
              className="toptool-btn"
              title="Compare or add symbol on the active pane"
              aria-label="Compare symbols"
              onClick={chart.openCompare}
            >
              <Icon name="compare" size={13} />
            </button>
          )}
          {chart.openTemplates && (
            <button
              type="button"
              className="toptool-btn"
              title="Chart templates: save or apply the whole look of the active pane"
              aria-label="Chart templates"
              onClick={chart.openTemplates}
            >
              <Icon name="template" size={13} />
            </button>
          )}
          <button
            type="button"
            className="toptool-btn"
            title={`Chart layout and sync (${snap.layoutName})`}
            aria-label="Chart layout"
            onClick={chart.openLayoutPicker}
          >
            <Icon name="layout" size={13} />
            {snap.paneCount > 1 && (
              <span className="toptool-sub">
                {String(snap.pane + 1)}/{String(snap.paneCount)}
              </span>
            )}
          </button>
        </>
      ) : (
        <span className="toptool-hint muted">{noChart}</span>
      )}
      <span className="toptool-sep" aria-hidden="true" />
      <button
        type="button"
        className="toptool-btn"
        title={`Orders module (${String(view.workingOrders ?? "unconfirmed")} working)`}
        onClick={() => {
          const d = dock();
          if (d) workspaceActions.focusOrAdd(d, "orders");
        }}
      >
        <Icon name="orders" size={13} /> Orders
        {view.workingOrders !== null && view.workingOrders > 0 && (
          <span className="toptool-count">{view.workingOrders}</span>
        )}
      </button>
      <button
        type="button"
        className="toptool-btn"
        title={`Positions module (${String(view.openPositions ?? "unconfirmed")} open${view.allSelected ? ", all accounts" : ""})`}
        onClick={() => {
          const d = dock();
          if (d) workspaceActions.focusOrAdd(d, "positions");
        }}
      >
        <Icon name="positions" size={13} /> Positions
        {view.openPositions !== null && view.openPositions > 0 && (
          <span className="toptool-badge" aria-label={`${String(view.openPositions)} open`}>
            {view.openPositions}
          </span>
        )}
      </button>
      <span className="spacer" />
      <button
        type="button"
        className="toptool-btn"
        title="Alerts (right sidebar): log, rules, watchers"
        onClick={() => {
          openSidebarTab("alerts");
        }}
      >
        <Icon name="bell" size={13} /> Alert
      </button>
      <span className="toptool-sep" aria-hidden="true" />
      <button
        type="button"
        className={ws.locked ? "toptool-btn on" : "toptool-btn"}
        aria-pressed={ws.locked}
        title={
          ws.locked
            ? "Layout locked: modules can't be dragged, resized or closed. Click to unlock."
            : "Lock layout (prevents accidental drags, resizes and closes)"
        }
        onClick={() => {
          store.update((d) => setWorkspaceLocked(d, ws.id, !ws.locked));
        }}
      >
        <Icon name={ws.locked ? "lock" : "unlock"} size={13} /> {ws.locked ? "Layout locked" : "Lock layout"}
      </button>
      <AddModuleButton dock={dock} compact />
    </div>
  );
}

/** "nq @ rithmic ▾": shows the active chart's symbol; click to type or search a new one. */
function SymbolBox({
  symbol,
  disabled,
  title,
  onCommit,
}: {
  symbol: string | null;
  disabled: boolean;
  title: string;
  onCommit: (s: string) => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const btn = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button
        ref={btn}
        type="button"
        className={open ? "toptool-symbol active" : "toptool-symbol"}
        aria-haspopup="dialog"
        aria-expanded={open}
        disabled={disabled}
        title={title}
        onClick={() => {
          setOpen((o) => !o);
        }}
      >
        <span className="toptool-sym">{symbol ? symbol.toLowerCase() : "—"}</span>
        <span className="toptool-feed">@ {FEED}</span>
        <Icon name="chevronDown" size={11} />
      </button>
      {open && (
        <SymbolSearchMenu
          anchor={btn.current}
          onPick={(s) => {
            setOpen(false);
            onCommit(s);
          }}
          onClose={() => {
            setOpen(false);
          }}
        />
      )}
    </>
  );
}

function SymbolSearchMenu({
  anchor,
  onPick,
  onClose,
}: {
  anchor: HTMLElement | null;
  onPick: (s: string) => void;
  onClose: () => void;
}): React.JSX.Element {
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const [pos, setPos] = useState({ left: 0, top: 0 });
  const box = useRef<HTMLDivElement>(null);
  const matches: SymbolMatch[] = searchSymbols(q, 10);
  useLayoutEffect(() => {
    const r = anchor?.getBoundingClientRect();
    if (r) setPos({ left: r.left, top: r.bottom + 4 });
  }, [anchor]);
  useEffect(() => {
    const onDown = (e: PointerEvent): void => {
      const t = e.target as Node | null;
      if (t && (box.current?.contains(t) || anchor?.contains(t))) return;
      onClose();
    };
    window.addEventListener("pointerdown", onDown, true);
    return () => {
      window.removeEventListener("pointerdown", onDown, true);
    };
  }, [anchor, onClose]);
  const commit = (): void => {
    const typed = q.trim().toUpperCase();
    const m = matches[hi];
    // The highlighted match (a known root or contract); text HELM doesn't know is sent as typed.
    if (m) onPick(m.symbol);
    else if (typed) onPick(typed);
  };
  return createPortal(
    <div
      ref={box}
      className="popover toptool-search"
      role="dialog"
      aria-label="Symbol search"
      style={{ position: "fixed", left: pos.left, top: pos.top }}
    >
      <input
        autoFocus
        className="input input-sm"
        value={q}
        placeholder="Symbol: NQ, ES, CL, 6E, NQH7…"
        aria-label="Symbol"
        spellCheck={false}
        onChange={(e) => {
          setQ(e.target.value);
          setHi(0);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHi((h) => Math.min(matches.length - 1, h + 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHi((h) => Math.max(0, h - 1));
          } else if (e.key === "Enter") {
            e.preventDefault();
            commit();
          } else if (e.key === "Escape") onClose();
        }}
      />
      <ul className="wl-results" role="listbox" aria-label="Matches">
        {matches.map((m, i) => (
          <li
            key={`${m.kind}:${m.symbol}`}
            role="option"
            aria-selected={i === hi}
            className={i === hi ? "wl-result active" : "wl-result"}
            onPointerEnter={() => {
              setHi(i);
            }}
            onClick={() => {
              onPick(m.symbol);
            }}
          >
            <span className="sym">{m.symbol}</span>
            <span className="wl-result-name">{m.entry.name}</span>
            <span className="muted wl-result-ex">{m.entry.exchange}</span>
          </li>
        ))}
      </ul>
    </div>,
    document.body,
  );
}
